/**
 * The fraud review queue (payments doc 15 §7) and the rules that feed it (§11).
 *
 * **Oldest case first.** The SLA clock is the sort order, and a case past
 * `risk.review_sla_minutes` says so on its row — the server decides that, not this screen.
 *
 * **A case explains itself.** Its score is the sum of the rules that fired, and the detail lists
 * each one with the value it saw and its weight. A score with no reasons is one nobody can defend
 * to the devotee who contests it.
 *
 * **Clear and confirm are final; escalate is not.** An escalated case comes back to the queue for
 * the senior reviewer's decision.
 *
 * **Freeze suspends the devotee, so it asks first** (R122). It needs L3, and the server refuses it
 * while the account has a paid booking still to run — no money stranded behind a locked account.
 * It is undone from Users (Restore sign-in), not here.
 *
 * **Block lists sit under the disputes** (`./BlockLists`): confirm fraud fills them, and a value
 * can be added or taken off by hand.
 *
 * **Chargebacks sit beside the queue** (`./Chargebacks`): a gateway dispute opens or joins a case
 * here, and its response pack is read from the same screen.
 *
 * **Rules are rows.** Threshold, weight, action and the on/off switch move here, each with a reason
 * for the audit trail. While `risk.enforce` is off (Settings) a block opens a case and nothing more.
 */
import { useState } from 'react';
import { Button, StatusChip } from '../components/primitives';
import { Input, Select, Toggle } from '../components/forms';
import { DataTable, Pager } from '../components/data';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { ConfirmAction, ScopeAction, Section } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import {
  useDecideRiskCase,
  useOpenRiskCase,
  useRiskCases,
  useRiskRules,
  useUpdateRiskRule,
  type RiskCaseRow,
  type RiskRuleRow,
} from '../lib/api/hooks/risk';
import { BlockLists } from './BlockLists';
import { Chargebacks } from './Chargebacks';
import { formatDateTime } from '../lib/datetime';

const DECISIONS = ['clear', 'confirm_fraud', 'escalate', 'freeze'] as const;
type Decision = (typeof DECISIONS)[number];
const SUBJECTS = ['booking', 'user', 'pandit', 'payment'] as const;
type Subject = (typeof SUBJECTS)[number];
const ACTIONS = ['score', 'review', 'block'] as const;
type Action = (typeof ACTIONS)[number];

const label = (value: string) => value.replace(/_/g, ' ');

/** What a case's status means to somebody scanning the column. */
export function caseTone(status: RiskCaseRow['status']): 'neutral' | 'warn' | 'success' | 'error' {
  if (status === 'cleared') return 'success';
  if (status === 'confirmed_fraud' || status === 'frozen') return 'error';
  if (status === 'escalated') return 'warn';
  return 'neutral';
}

/* ──────────────────────────────  One case  ────────────────────────────── */

function CaseDetail({ item, onClose }: { item: RiskCaseRow; onClose: () => void }) {
  const { scope, toast } = useConsole();
  const decide = useDecideRiskCase();
  const [decision, setDecision] = useState<Decision>('clear');
  const [notes, setNotes] = useState('');
  const [confirmingFreeze, setConfirmingFreeze] = useState(false);
  const final = item.status === 'cleared' || item.status === 'confirmed_fraud' || item.status === 'frozen';

  const send = () =>
    decide.mutate(
      { code: item.code, decision, notes: notes.trim() },
      {
        onSuccess: (row) => {
          toast({ tone: 'success', message: `${row.code} ${label(row.status)}` });
          setNotes('');
          setConfirmingFreeze(false);
          onClose();
        },
        onError: (e) => toast({ tone: 'error', message: errorMessage(e) }),
      },
    );

  return (
    <Section
      title={`${item.code} · ${item.subject_type} #${item.subject_id}`}
      note={item.score === null ? 'opened by hand — no score' : `score ${item.score}`}
      actions={
        <Button variant="secondary" size="sm" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div style={{ display: 'grid', gap: 'var(--sp-md)' }}>
        {item.fired.length === 0 ? (
          <span>No rule fired — {item.opened_by} opened it.</span>
        ) : (
          <DataTable
            density="compact"
            columns={[
              { key: 'rule', label: 'Rule', mono: true },
              { key: 'value', label: 'Saw', align: 'right', mono: true },
              { key: 'weight', label: 'Weight', align: 'right', mono: true },
            ]}
            rows={item.fired.map((f) => ({ id: f.rule, rule: f.rule, value: f.value, weight: f.weight }))}
          />
        )}
        {item.hold && (
          <div data-testid="risk-hold" style={{ display: 'grid', gap: 'var(--sp-xs)' }}>
            <strong>Payment held for review</strong>
            <span>{`${item.hold.booking_code} · slot ${formatDateTime(item.hold.slot_at)}`}</span>
            <span>{`Escalates to a senior reviewer at ${formatDateTime(item.hold.escalate_at)} if still undecided.`}</span>
            <span>
              Clear confirms the booking, or cancels it with a full refund if the slot has passed. Confirm fraud
              cancels it with a full refund.
            </span>
          </div>
        )}
        {item.resolution_notes && <span>{item.resolution_notes}</span>}
        {!final && (
          <div style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'end', flexWrap: 'wrap' }}>
            <Select
              label="Decision"
              value={decision}
              options={DECISIONS}
              optionLabel={(code) => code.replace(/_/g, ' ')}
              onChange={(v) => setDecision(v as Decision)}
            />
            <Input label="Why" value={notes} onChange={setNotes} style={{ flex: 1 }} />
            <ScopeAction
              scope={scope}
              need="finance"
              size="sm"
              variant="secondary"
              disabled={notes.trim().length < 10 || decide.isPending}
              onClick={decision === 'freeze' ? () => setConfirmingFreeze(true) : send}
            >
              Decide
            </ScopeAction>
          </div>
        )}
      </div>
      <ConfirmAction
        open={confirmingFreeze}
        title={`Freeze the account behind ${item.code}?`}
        phrase={`FREEZE ${item.code}`}
        tone="error"
        consequences={[
          'Suspends the devotee: they cannot sign in until someone restores it from Users.',
          'Refused while the account has a paid booking not yet performed or cancelled — cancel or refund those first.',
          'Freezing needs L3 authority; the server refuses anyone below it.',
          "It does not put the devotee's installs or cards on the block lists — confirm fraud does that.",
        ]}
        confirmLabel="Freeze the account"
        onCancel={() => setConfirmingFreeze(false)}
        onConfirm={send}
      />
    </Section>
  );
}

/* ──────────────────────────────  Opening by hand  ────────────────────────────── */

function OpenCase() {
  const { scope, toast } = useConsole();
  const open = useOpenRiskCase();
  const [subject, setSubject] = useState<Subject>('booking');
  const [id, setId] = useState('');
  const [notes, setNotes] = useState('');
  const idError = id === '' || /^[1-9]\d*$/.test(id) ? undefined : 'A number';
  const ready = /^[1-9]\d*$/.test(id) && notes.trim().length >= 10;

  const send = () =>
    open.mutate(
      { subject_type: subject, subject_id: Number(id), notes: notes.trim() },
      {
        onSuccess: (row) => {
          toast({ tone: 'success', message: `${row.code} opened` });
          setId('');
          setNotes('');
        },
        onError: (e) => toast({ tone: 'error', message: errorMessage(e) }),
      },
    );

  return (
    <Section title="Open a case" note="for what the scorer cannot see — a tip-off, a recon break">
      <div style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'end', flexWrap: 'wrap' }}>
        <Select
          label="About a"
          value={subject}
          options={SUBJECTS}
          onChange={(v) => setSubject(v as Subject)}
        />
        <Input label="Id" value={id} onChange={setId} error={idError} inputMode="numeric" />
        <Input label="Why" value={notes} onChange={setNotes} style={{ flex: 1 }} />
        <ScopeAction
          scope={scope}
          need="finance"
          size="sm"
          variant="secondary"
          disabled={!ready || open.isPending}
          onClick={send}
        >
          Open
        </ScopeAction>
      </div>
    </Section>
  );
}

/* ──────────────────────────────  Rules  ────────────────────────────── */

function RuleEditor({ rule, onClose }: { rule: RiskRuleRow; onClose: () => void }) {
  const { scope, toast } = useConsole();
  const update = useUpdateRiskRule();
  const [threshold, setThreshold] = useState(String(rule.threshold));
  const [weight, setWeight] = useState(String(rule.score_weight));
  const [action, setAction] = useState<Action>(rule.action);
  const [active, setActive] = useState(rule.is_active);
  const [reason, setReason] = useState('');

  const number = /^\d+(\.\d+)?$/;
  const thresholdError = number.test(threshold) ? undefined : 'A number';
  const weightError = /^\d+$/.test(weight) ? undefined : 'A whole number';
  const changes = {
    ...(Number(threshold) !== rule.threshold ? { threshold: Number(threshold) } : {}),
    ...(Number(weight) !== rule.score_weight ? { score_weight: Number(weight) } : {}),
    ...(action !== rule.action ? { action } : {}),
    ...(active !== rule.is_active ? { is_active: active } : {}),
  };
  const ready =
    !thresholdError && !weightError && Object.keys(changes).length > 0 && reason.trim().length >= 10;

  const save = () =>
    update.mutate(
      // Only what moved: an unchanged field sent anyway is a change in the audit diff that wasn't.
      { ruleKey: rule.rule_key, reason: reason.trim(), ...changes },
      {
        onSuccess: () => {
          toast({ tone: 'success', message: `${rule.rule_key} saved` });
          onClose();
        },
        onError: (e) => toast({ tone: 'error', message: errorMessage(e) }),
      },
    );

  return (
    <Section
      title={rule.rule_key}
      note={`${rule.description} · fires when ${rule.signal} is ${rule.below ? 'below' : 'at or above'} the threshold`}
      actions={
        <Button variant="secondary" size="sm" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'end', flexWrap: 'wrap' }}>
        <Input label="Threshold" value={threshold} onChange={setThreshold} error={thresholdError} />
        <Input label="Weight" value={weight} onChange={setWeight} error={weightError} />
        <Select label="Action" value={action} options={ACTIONS} onChange={(v) => setAction(v as Action)} />
        <Toggle label="Active" on={active} onChange={setActive} />
        <Input label="Why" value={reason} onChange={setReason} style={{ flex: 1 }} />
        <ScopeAction
          scope={scope}
          need="finance"
          size="sm"
          variant="secondary"
          disabled={!ready || update.isPending}
          onClick={save}
        >
          Save
        </ScopeAction>
      </div>
    </Section>
  );
}

function Rules() {
  const rules = useRiskRules();
  const [editing, setEditing] = useState<string | null>(null);
  /* Looked up on every render, so a save shows the server's copy rather than the one clicked. */
  const editingRule = rules.data?.items.find((r) => r.rule_key === editing);

  return (
    <>
      <Section title="Rules" note="every threshold and weight is a row — click one to tune it" pad={false}>
        {rules.isPending && <LoadingState message="Reading the rules…" />}
        {rules.isError && <ErrorState title="Could not read the rules" cause={errorMessage(rules.error)} />}
        {rules.isSuccess && (
          <DataTable
            density="compact"
            onRowClick={(row) => setEditing(String(row.id))}
            columns={[
              { key: 'rule', label: 'Rule', mono: true },
              { key: 'threshold', label: 'Threshold', align: 'right', mono: true },
              { key: 'weight', label: 'Weight', align: 'right', mono: true },
              { key: 'action', label: 'Action' },
              { key: 'state', label: '' },
            ]}
            rows={rules.data.items.map((r) => ({
              id: r.rule_key,
              rule: r.rule_key,
              threshold: `${r.below ? '<' : '≥'} ${r.threshold}`,
              weight: r.score_weight,
              action: r.action,
              state: <StatusChip tone={r.is_active ? 'success' : 'neutral'} label={r.is_active ? 'on' : 'off'} />,
            }))}
          />
        )}
      </Section>
      {editingRule && <RuleEditor key={editingRule.rule_key} rule={editingRule} onClose={() => setEditing(null)} />}
    </>
  );
}

/* ──────────────────────────────  The queue  ────────────────────────────── */

export function Risk() {
  const [includeDecided, setIncludeDecided] = useState(false);
  const [page, setPage] = useState(1);
  const [openCode, setOpenCode] = useState<string | null>(null);
  const cases = useRiskCases(includeDecided, page);
  const filter = (on: boolean) => {
    setIncludeDecided(on);
    setPage(1);
  };
  const openCase = cases.data?.items.find((c) => c.code === openCode);
  const items = cases.data?.items ?? [];

  return (
    <>
      {/* Above the queue, not under it: below fifty rows the case opened off-screen and the click
          looked dead (found live). */}
      {openCase && <CaseDetail key={openCase.code} item={openCase} onClose={() => setOpenCode(null)} />}
      <Section
        title="Review queue"
        note="oldest first — the SLA clock is the order"
        actions={<Toggle label="Show decided" on={includeDecided} onChange={filter} />}
        pad={false}
      >
        {cases.isPending && <LoadingState message="Reading the queue…" />}
        {cases.isError && <ErrorState title="Could not read the queue" cause={errorMessage(cases.error)} />}
        {cases.isSuccess && items.length === 0 && (
          <EmptyState inline icon="shield-check" title="Nothing to review" body="No case is waiting on a decision." />
        )}
        {items.length > 0 && (
          <DataTable
            density="compact"
            onRowClick={(row) => setOpenCode(String(row.id))}
            columns={[
              { key: 'code', label: 'Case', mono: true },
              { key: 'subject', label: 'About' },
              { key: 'score', label: 'Score', align: 'right', mono: true },
              { key: 'status', label: 'Status' },
              { key: 'sla', label: 'Clock' },
              { key: 'opened', label: 'Opened', mono: true },
            ]}
            rows={items.map((c) => ({
              id: c.code,
              code: c.code,
              subject: c.hold ? (
                <>
                  {`${c.subject_type} #${c.subject_id} `}
                  <StatusChip tone="warn" label="payment held" />
                </>
              ) : (
                `${c.subject_type} #${c.subject_id}`
              ),
              score: c.score ?? '—',
              status: <StatusChip tone={caseTone(c.status)} label={label(c.status)} />,
              sla:
                c.sla === 'decided' ? '—' : (
                  <StatusChip tone={c.sla === 'breached' ? 'error' : 'neutral'} label={label(c.sla)} />
                ),
              opened: formatDateTime(c.opened_at),
            }))}
          />
        )}
        {cases.data && (
          <Pager page={page} pageSize={cases.data.page_size} total={cases.data.total} onPage={setPage} />
        )}
      </Section>
      <Chargebacks />
      <BlockLists />
      <OpenCase />
      <Rules />
    </>
  );
}
